import { createContext, createSignal, useContext, type JSX } from 'solid-js';
import { createStore } from 'solid-js/store';
import { idString, type BrowserOrServerId } from 'Browser/BrowserId';
import type { Poster as PosterDesign } from 'Shared/posters/project/posterSchema';
import type { DataRecord } from 'Shared/posters/types';
import type { IThemeInfo } from 'Shared/backend/eventPage/EventPage';

/*
	A poster that has outlived the page which was editing it: a deeply-copied description of
	everything needed to mount, capture and upload it, holding no reference back into that page's
	store, resources or DOM. The renderer keeps one of these mounted offscreen until its upload
	lands, which is what lets leaving an event page be instant — nothing has to be captured before
	the navigation is allowed through.
*/
export interface IDetachedRender {
	key:           string;
	docId:         BrowserOrServerId;
	uploadUrl:     string;
	/* The image fields the record owns, or undefined when it owns the whole image (see posterImageData) */
	posterFields:  string[]|undefined;
	/* Absent for legacy-builder events, which render from the record alone */
	design:        PosterDesign|undefined;
	record:        DataRecord;
	themeInfo:     IThemeInfo|undefined;
	useLegacy:     boolean;
	legacyPoster:  (record:DataRecord) => JSX.Element;
	legacyCapture: (posterNode:HTMLDivElement) => Promise<Blob>;
}

const PosterRendererContext = createContext();

export function PosterRendererProvider(P)
{
	const [config,setConfig] = createSignal();

	/*
		The active renderer's chance to hand its poster to a background render before the page that
		owns the data goes away. Held here, and called from the setter rather than left to the
		renderer's own onCleanup, so that it is guaranteed to run on the one event that matters -
		the config being cleared or replaced. A renderer disposing itself is not the same event:
		moving from one event page straight to another swaps the config while leaving the renderer
		mounted throughout, so a cleanup-based handover would never fire.
	*/
	let handover: (()=>void)|undefined;

	const replaceConfig = (value: Parameters<typeof setConfig>[0]) => {
		handover?.();
		handover = undefined;
		setConfig(value);
	};

	const setHandover = (fn:()=>void) => { handover = fn; };

	/* Identity-checked: a departing renderer's cleanup must not wipe a handover its replacement has
	   already registered. */
	const clearHandover = (fn:()=>void) => { if (handover==fn) handover = undefined; };

	/* Posters still being captured and uploaded after their page has gone. Plain immutable
	   snapshots, so <For> can key on them directly. */
	const [detachedRenders,setDetachedRenders] = createSignal<IDetachedRender[]>([]);
	let nextDetachedKey = 0;

	const addDetachedRender = (render:Omit<IDetachedRender,'key'>) =>
		setDetachedRenders(renders => [...renders, {...render, key:String(nextDetachedKey++)}]);

	const removeDetachedRender = (key:string) =>
		setDetachedRenders(renders => renders.filter(render => render.key!=key));

	/* The active renderer's capture-and-upload, registered here so that pages can ask for a render
	   at a point of their choosing (eg on publishing) without reaching into the renderer. */
	const [render,setRender] = createSignal<(()=>Promise<void>)|undefined>();

	/* Per-event poster render/upload status, keyed by document id. Lives here, above both the
	   event page (which does the rendering) and the events page (which shows the thumbnails), so
	   the events list can react to an upload started while the user was editing. */
	const [uploads,setUploads] = createStore({});

	const markRendering = (docId:BrowserOrServerId) =>
		setUploads(idString(docId),{status:'rendering'});
	const markRendered = (docId:BrowserOrServerId,poster) =>
		setUploads(idString(docId),{status:'rendered',poster});
	const clearRendering = (docId:BrowserOrServerId) =>
		setUploads(idString(docId),undefined);

	return (
		<PosterRendererContext.Provider value={{
			config, setConfig:replaceConfig, setHandover, clearHandover,
			detachedRenders, addDetachedRender, removeDetachedRender,
			render, setRender, uploads, markRendering, markRendered, clearRendering
		}}>
			{P.children}
		</PosterRendererContext.Provider>
	);
}

export function usePosterRenderer()
{
	return useContext(PosterRendererContext).config;
}

export function useSetPosterRenderer()
{
	return useContext(PosterRendererContext).setConfig;
}

/* Register what should be handed to a background render when this page's config goes away */
export function usePosterHandover()
{
	const ctx = useContext(PosterRendererContext);
	return {setHandover:ctx.setHandover, clearHandover:ctx.clearHandover};
}

export function useDetachedRenders()
{
	return useContext(PosterRendererContext).detachedRenders;
}

export function useAddDetachedRender()
{
	return useContext(PosterRendererContext).addDetachedRender;
}

export function useRemoveDetachedRender()
{
	return useContext(PosterRendererContext).removeDetachedRender;
}

/* Render the poster now, if there's a renderer mounted and its poster is out of date */
export function useRenderPoster()
{
	const ctx = useContext(PosterRendererContext);
	return async () => await ctx.render()?.();
}

export function useSetRenderPoster()
{
	return useContext(PosterRendererContext).setRender;
}

export function usePosterUploads()
{
	return useContext(PosterRendererContext).uploads;
}

export function usePosterUploadTracker()
{
	const ctx = useContext(PosterRendererContext);
	return {markRendering:ctx.markRendering, markRendered:ctx.markRendered, clearRendering:ctx.clearRendering};
}
